// IMP-337 (FE-C2) — 라우트 코드 스플리팅 청크 로드 재시도 헬퍼.
// 배포 직후 CDN/캐시 정합성 문제로 청크 로드가 실패하는 경우(오래된 index.html이 참조하는
// 해시된 청크 파일이 더 이상 존재하지 않는 경우) 1회 자동 새로고침으로 복구를 시도한다.

import React from 'react';

/**
 * 청크 import 를 시도하고, 실패 시 세션당 1회 새로고침으로 재시도한다.
 * @param {() => Promise<any>} importFn - 동적 import 함수
 * @param {string} key - 청크 식별 키 (sessionStorage 플래그 구분용)
 * @param {{ reload?: () => void, storage?: Storage }} [deps] - 테스트 주입용 의존성
 * @returns {Promise<any>}
 */
export async function loadWithRetry(importFn, key, deps = {}) {
    const { reload = () => window.location.reload(), storage = window.sessionStorage } = deps;
    const flag = `chunk-retry:${key}`;

    let hasFlag = false;
    try {
        hasFlag = !!storage.getItem(flag);
    } catch (err) {
        // storage 접근 불가 환경(프라이빗 모드 등) — 플래그 없는 것으로 간주하고 진행
        console.warn('[lazyWithRetry] storage.getItem failed', { key, reason: err?.message });
    }

    try {
        const mod = await importFn();
        try {
            storage.removeItem(flag);
        } catch (err) {
            console.warn('[lazyWithRetry] storage.removeItem failed', { key, reason: err?.message });
        }
        return mod;
    } catch (err) {
        if (hasFlag) {
            try {
                storage.removeItem(flag);
            } catch (removeErr) {
                console.warn('[lazyWithRetry] storage.removeItem failed', {
                    key,
                    reason: removeErr?.message,
                });
            }
            throw err;
        }

        try {
            storage.setItem(flag, '1');
        } catch (setErr) {
            console.warn('[lazyWithRetry] storage.setItem failed', { key, reason: setErr?.message });
        }
        reload();
        return new Promise(() => {});
    }
}

/**
 * React.lazy 래퍼 — 청크 로드 실패 시 loadWithRetry 로 자동 재시도한다.
 * @param {() => Promise<any>} importFn - 동적 import 함수
 * @param {string} key - 청크 식별 키
 */
export default function lazyWithRetry(importFn, key) {
    return React.lazy(() => loadWithRetry(importFn, key));
}
